/**
 * ColumnConfigPopover — Configuration colonnes (afficher/masquer + drag-drop).
 * Port BE-TAYE adapté SaaS. LocalStorage clé "contracts_columns_config".
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Settings2, GripVertical } from "lucide-react";

export const DEFAULT_COLUMNS = [
  { id: "societe", label: "Société", visible: true, sortable: true },
  { id: "type", label: "Type", visible: true },
  { id: "etat", label: "État", visible: true },
  { id: "status", label: "Statut", visible: true },
  { id: "produit", label: "Produit", visible: true },
  { id: "abonnement_gi", label: "Abo. Gestion Int.", visible: true, align: "right" },
  { id: "mrr", label: "MRR", visible: true, align: "right" },
  { id: "vendeur", label: "Vendeur", visible: true },
  { id: "revendeur", label: "Revendeur", visible: true },
  { id: "leaser", label: "Leaser", visible: true },
  { id: "date_fin", label: "Date fin", visible: true },
  { id: "ca_leaser", label: "CA Leaser", visible: true, align: "right" },
  { id: "date_installation", label: "Date installation", visible: false },
  { id: "duree_mois", label: "Durée (mois)", visible: false },
  { id: "commission", label: "Commission", visible: false, align: "right" },
  { id: "abonnement_fin", label: "Abo. Finance", visible: false, align: "right" },
  { id: "date_demande_resiliation", label: "Date demande résiliation", visible: false },
  { id: "date_effet_resiliation", label: "Date effet résiliation", visible: false },
];

const COLUMNS_CONFIG_VERSION = 2;
const CONFIG_STORAGE_KEY = "clarix_contracts_columns_config";
const VERSION_STORAGE_KEY = "clarix_contracts_columns_config_version";

export const loadColumnsConfig = () => {
  try {
    const savedVersion = parseInt(localStorage.getItem(VERSION_STORAGE_KEY) || "1", 10);
    const saved = localStorage.getItem(CONFIG_STORAGE_KEY);
    if (savedVersion < COLUMNS_CONFIG_VERSION) {
      localStorage.setItem(VERSION_STORAGE_KEY, String(COLUMNS_CONFIG_VERSION));
      localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(DEFAULT_COLUMNS));
      return DEFAULT_COLUMNS;
    }
    if (saved) {
      const parsed = JSON.parse(saved);
      const merged = DEFAULT_COLUMNS.map((defCol) => {
        const savedCol = parsed.find((c) => c.id === defCol.id);
        return savedCol ? { ...defCol, ...savedCol } : defCol;
      });
      const orderedIds = parsed.map((c) => c.id);
      merged.sort((a, b) => {
        const iA = orderedIds.indexOf(a.id);
        const iB = orderedIds.indexOf(b.id);
        if (iA === -1) return 1;
        if (iB === -1) return -1;
        return iA - iB;
      });
      return merged;
    }
  } catch (e) { console.error(e); }
  return DEFAULT_COLUMNS;
};

export const saveColumnsConfig = (columns) => {
  try {
    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(columns));
    localStorage.setItem(VERSION_STORAGE_KEY, String(COLUMNS_CONFIG_VERSION));
  } catch (e) { console.error(e); }
};

export const reorderColumns = (columns, dragIndex, dropIndex) => {
  if (dragIndex === null || dragIndex === dropIndex) return columns;
  const newColumns = [...columns];
  const [dragged] = newColumns.splice(dragIndex, 1);
  newColumns.splice(dropIndex, 0, dragged);
  return newColumns;
};

export default function ColumnConfigPopover({ columns, setColumns }) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const toggleColumn = (id) => {
    const next = columns.map((c) => c.id === id ? { ...c, visible: !c.visible } : c);
    setColumns(next); saveColumnsConfig(next);
  };
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.style.opacity = "0.5";
  };
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    setDraggedIndex(null); setDragOverIndex(null);
  };
  const handleDragOver = (e, index) => {
    e.preventDefault(); e.dataTransfer.dropEffect = "move";
    if (index !== draggedIndex) setDragOverIndex(index);
  };
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    const next = reorderColumns(columns, draggedIndex, dropIndex);
    setColumns(next); saveColumnsConfig(next);
    setDraggedIndex(null); setDragOverIndex(null);
  };
  const resetToDefault = () => {
    setColumns([...DEFAULT_COLUMNS]); saveColumnsConfig(DEFAULT_COLUMNS);
  };
  const visibleCount = columns.filter((c) => c.visible).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="column-config-btn">
          <Settings2 className="w-4 h-4" />
          <span className="hidden sm:inline">Colonnes</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{visibleCount}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Configurer les colonnes</h4>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={resetToDefault}>Réinitialiser</Button>
          </div>
          <p className="text-xs text-slate-500 mt-1">Glissez pour réorganiser, cochez pour afficher</p>
        </div>
        <div className="max-h-[400px] overflow-y-auto p-2">
          {columns.map((col, idx) => (
            <div key={col.id} draggable
              onDragStart={(e) => handleDragStart(e, idx)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDrop={(e) => handleDrop(e, idx)}
              className={`flex items-center gap-2 p-2 rounded-md cursor-move transition-colors
                ${dragOverIndex === idx ? "bg-blue-50 border-2 border-blue-300 border-dashed" : "hover:bg-slate-50"}
                ${draggedIndex === idx ? "opacity-50" : ""}`}
              data-testid={`column-item-${col.id}`}>
              <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Checkbox id={`col-${col.id}`} checked={col.visible}
                onCheckedChange={() => toggleColumn(col.id)} className="flex-shrink-0" />
              <Label htmlFor={`col-${col.id}`} className="text-sm cursor-pointer flex-1 select-none">{col.label}</Label>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
